import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  message,
  Tabs,
} from "antd";
import {
  FilterOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getInfractionsStatistics } from "../../../api/statistics/statisticsRequest";
import { getZones } from "../../../api/zone/zoneRequest";
import { getTypeInfraction } from "../../../api/typeInfraction/infractionType";
import { formatAmountInFrench, statusText } from "../../../utils/constants";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const InfractionsStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedDateField, setSelectedDateField] =
    useState("datetime_infraction");
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedtypeInfractions, setSelectedTypeInfractions] = useState(null);
  const [selectedGroupBy, setSelectedGroupBy] = useState(null);

  const [zones, setZones] = useState([]);
  const [typeInfractions, setTypeInfractions] = useState([]);
  const [infractionsData, setInfractionsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchZones();
    fetchTypeInfractions();
  }, []);

  const filterInfractionsStatistics = async () => {
    setLoading(true);

    try {
      const data = await getInfractionsStatistics({
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        zone_id: selectedZone,
        type_infraction: selectedtypeInfractions,
        group_by: selectedGroupBy,
        date_field: selectedDateField,
      });
      setInfractionsData(data); // Update state with the fetched data
    } catch (error) {
      setInfractionsData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    setLoading(true);
    try {
      const data = await getZones();
      setZones(data.data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des zones");
    } finally {
      setLoading(false);
    }
  };

  const fetchTypeInfractions = async () => {
    setLoading(true);
    try {
      const data = await getTypeInfraction();
      setTypeInfractions(data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des types d'infractions");
    } finally {
      setLoading(false);
    }
  };

  const pieChartOptions = {
    chart: {
      type: "pie",
    },
    title: {
      text: "Infractions par Type",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      pointFormat:
        "Nombre: <b>{point.y}</b><br>Montant total: <b>{point.total_amount:.2f}</b>",
    },
    series: [
      {
        name: "Infractions",
        data: infractionsData
          ? infractionsData.by_type.map((item) => ({
              name: item.name,
              y: item.count,
              total_amount: formatAmountInFrench(parseFloat(item.total_amount)),
            }))
          : [],
      },
    ],
  };

  const timelineChartOptions = {
    chart: {
      type: "column",
      zoomType: "x",
    },
    title: {
      text: "Chronologie des Infractions",
    },
    xAxis: {
      categories: infractionsData
        ? infractionsData.timeline.map((item) => item.date)
        : [],
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: {
        text: "Nombre",
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: "Infractions Totales",
        data: infractionsData
          ? infractionsData.timeline.map((item) => item.count)
          : [],
      },
      {
        name: "Infractions Payées",
        data: infractionsData
          ? infractionsData.timeline.map((item) => parseFloat(item.paid_count))
          : [],
      },
    ],
  };

  const hourlyChartOptions = {
    chart: {
      type: "line",
    },
    title: {
      text: "Distribution Horaire des Infractions",
    },
    xAxis: {
      categories: infractionsData
        ? infractionsData.hourly_distribution.map((item) => item.hour)
        : [],
      title: {
        text: "Heure de la journée",
      },
    },
    yAxis: {
      title: {
        text: "Nombre d'Infractions",
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: "Infractions Totales",
        data: infractionsData
          ? infractionsData.hourly_distribution.map(
              (item) => item.total_infractions
            )
          : [],
      },
      {
        name: "Infractions Payées",
        data: infractionsData
          ? infractionsData.hourly_distribution.map((item) =>
              parseFloat(item.paid_infractions)
            )
          : [],
      },
    ],
  };

  const zoneChartOptions = {
    chart: {
      type: "column",
    },
    title: {
      text: "Statistiques par Contrat",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: infractionsData?.by_zone.map((zone) => zone.name),
      crosshair: true,
    },
    yAxis: [
      {
        min: 0,
        title: {
          text: "Nombre d'Infractions",
        },
      },
      {
        min: 0,
        title: {
          text: "Montant Total",
        },
        opposite: true,
      },
    ],
    tooltip: {
      shared: true,
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      backgroundColor: "rgba(255,255,255,0.8)",
      borderColor: "#CCC",
      borderWidth: 1,
      shadow: false,
    },
    series: [
      {
        name: "Nombre d'Infractions",
        data: infractionsData?.by_zone.map((zone) => zone.count),
        yAxis: 0,
      },
      {
        name: "Montant Total",
        data: infractionsData?.by_zone.map((zone) =>
          parseFloat(zone.pending_amount) + parseFloat(zone.paid_amount) + parseFloat(zone.cancelled_amount)
        ),
        yAxis: 1,
        tooltip: {
          valueDecimals: 2,
          valuePrefix: "",
          valueSuffix: " ",
        },
      },
    ],
  };

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Tabs
              activeKey={selectedDateField}
              onChange={(value) => setSelectedDateField(value)}
              items={[
                {
                  key: "datetime_infraction",
                  label: "Date d'infraction",
                },
                {
                  key: "payment_at",
                  label: "Date de paiement",
                },
                {
                  key: "cancelled_at",
                  label: "Date d'annulation",
                },
              ]}
            />
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  placeholder={["Date début", "Date fin"]}
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Sélectionner le type d'infractions"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedTypeInfractions(value)}
                  allowClear
                >
                  {typeInfractions.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Sélectionner la Contrat"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedZone(value)}
                  allowClear
                >
                  {zones.map((zone) => (
                    <Option key={zone.id} value={zone.id}>
                      {zone.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Groupé par"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedGroupBy(value)}
                  allowClear
                >
                  <Option value={"daily"}>Quotidien</Option>
                  <Option value={"weekly"}>Hebdomadaire</Option>
                  <Option value={"monthly"}>Mensuel</Option>
                </Select>
              </Col>
            </Row>
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterInfractionsStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        {infractionsData && (
          <Space direction="vertical" size="large" className="w-full">
            <Card className="shadow-sm">
              <Title level={2} className="text-center mb-6">
                Résumé des Infractions
              </Title>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Infractions Totales"
                    value={infractionsData.summary.total}
                    prefix={<FileTextOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Montant Total des Infractions"
                    value={parseFloat(
                      infractionsData.summary.total_amount
                    ).toFixed(2)}
                    prefix={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Infractions Payées"
                    value={infractionsData.summary.paid_count}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Infractions Annulées"
                    value={infractionsData.summary.cancelled_count}
                    prefix={<CloseCircleOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Card className="shadow-sm">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={pieChartOptions}
                  />
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card className="shadow-sm">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={timelineChartOptions}
                  />
                </Card>
              </Col>
            </Row>

            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={hourlyChartOptions}
              />
            </Card>

            <Card className="shadow-sm">
              <Title level={3} className="mb-4">
                Infractions par Statut
              </Title>
              <Table
                dataSource={infractionsData.by_status}
                columns={[
                  {
                    title: "Statut",
                    dataIndex: "status",
                    key: "status",
                    render: (text) => statusText(text),
                  },
                  {
                    title: "Nombre",
                    dataIndex: "count",
                    key: "count",
                    align: "center",
                    sorter: (a, b) => a.count - b.count,
                  },
                  {
                    title: "Montant Total",
                    dataIndex: "total_amount",
                    key: "total_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.total_amount) - parseFloat(b.total_amount),
                    render: (text) =>
                      `${formatAmountInFrench(parseFloat(text).toFixed(2))} `,
                  },
                ]}
                pagination={false}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Statistiques par Contrat
              </Title>
              <HighchartsReact
                highcharts={Highcharts}
                options={zoneChartOptions}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Détails des Statistiques par Contrat
              </Title>
              <Table
                dataSource={infractionsData?.by_zone}
                columns={[
                  { title: "Contrat", dataIndex: "name", key: "name" },
                  {
                    title: "Nombre Total",
                    dataIndex: "count",
                    key: "count",
                    align: "right",
                    sorter: (a, b) => a.count - b.count,
                  },

                  {
                    title: "Infractions Payées",
                    dataIndex: "paid_count",
                    key: "paid_count",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.paid_count) - parseFloat(b.paid_count),
                  },

                  {
                    title: "Montant Payé",
                    dataIndex: "paid_amount",
                    key: "paid_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.paid_amount) - parseFloat(b.paid_amount),
                    render: (text) => formatAmountInFrench(parseFloat(text)),
                  },
                  {
                    title: "Infractions en Attente",
                    dataIndex: "pending_count",
                    key: "pending_count",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.pending_count) - parseFloat(b.pending_count),
                  },

                  {
                    title: "Montant en Attente",
                    dataIndex: "pending_amount",
                    key: "pending_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.pending_amount) -
                      parseFloat(b.pending_amount),
                    render: (text) => formatAmountInFrench(parseFloat(text)),
                  },
                  {
                    title: "Infractions Annulées",
                    dataIndex: "cancelled_count",
                    key: "cancelled_count",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.cancelled_count) -
                      parseFloat(b.cancelled_count),
                  },

                  {
                    title: "Montant Annulé",
                    dataIndex: "cancelled_amount",
                    key: "cancelled_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.cancelled_amount) -
                      parseFloat(b.cancelled_amount),
                    render: (text) => formatAmountInFrench(parseFloat(text)),
                  },
                ]}
                pagination={false}
              />
            </Card>
          </Space>
        )}
      </Content>
    </Layout>
  );
};

export default InfractionsStatistics;
